<template>
  <section>
    <b-container class="mt-4">
      <b-row class="mb-2" align-v="end" align-h="end">
        <b-col class="py-2 pr-3">
          <header class="my-2">
            <h5 class="mb-0 font-weight-bold">Fees Management</h5>
          </header>
        </b-col>
        <b-col class="py-2 pl-3 pl-sm-1 pr-3" cols="auto">
          <b-button 
            class="my-2 mx-2" 
            @click="refresh" 
            :disabled="state.loading" 
            variant="dark" 
            size="sm"
          >
            <b-spinner v-if="state.loading" small class="mr-1" />
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <b-row no-gutters align-h="center">
        <transition name="component-fade" mode="out-in">
          <Line-Loader v-if="state.loading" />
        </transition>

        <transition-group name="component-group-fade" mode="out-in" class="w-100">
          <b-container 
            class="p-5 py-0 bg-light br-5" 
            key="empty-state" 
            v-if="!state.loading && allMerchantsFees.length < 1"
          >
            <div class="text-center py-4">
              <p class="mb-0">No merchants found at the moment.</p>
            </div>
          </b-container>

          <b-container key="data-table" v-else-if="!state.loading">
            <b-row>
              <b-table-simple 
                hover 
                small 
                responsive 
                bordered
                class="fees-table"
              >
                <b-thead class="bg-light">
                  <b-tr>
                    <b-th rowspan="2" class="text-center align-middle">#</b-th>
                    <b-th rowspan="2" class="text-center align-middle">Merchant ID</b-th>
                    <b-th rowspan="2" class="text-center align-middle">Name</b-th>
                    <b-th rowspan="2" class="text-center align-middle">Status</b-th>
                    <b-th colspan="4" class="text-center">Fee Structure</b-th>
                    <b-th rowspan="2" class="text-center align-middle">Verified</b-th>
                  </b-tr>
                  <b-tr>
                    <b-th class="text-center">MTN In</b-th>
                    <b-th class="text-center">MTN Out</b-th>
                    <b-th class="text-center">Airtel In</b-th>
                    <b-th class="text-center">Airtel Out</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr 
                    v-for="(merchant, index) in allMerchantsFees" 
                    :key="merchant.id"
                    class="table-row-clickable"
                    @click="openEditFeeModal(merchant)"
                  >
                    <b-td class="text-center">
                      {{ (pagination.currentPage - 1) * pagination.perPage + index + 1 }}
                    </b-td>
                    <b-td class="text-center">
                      <b-link class="text-primary font-weight-bold">
                        {{ merchant.id }}
                      </b-link>
                    </b-td>
                    <b-td>{{ merchant.name }}</b-td>
                    <b-td class="text-center">
                      <b-badge :variant="merchant.active ? 'success' : 'danger'">
                        {{ merchant.active ? "Active" : "Inactive" }}
                      </b-badge>
                    </b-td>
                    <b-td class="text-center">
                      <span class="fee-display">
                        {{ formatFeeDisplay(merchant.mtn_in, merchant.mtn_in_fee_type) }}
                      </span>
                    </b-td>
                    <b-td class="text-center">
                      <span class="fee-display">
                        {{ formatFeeDisplay(merchant.mtn_out, merchant.mtn_out_fee_type) }}
                      </span>
                    </b-td>
                    <b-td class="text-center">
                      <span class="fee-display">
                        {{ formatFeeDisplay(merchant.airtel_in, merchant.airtel_in_fee_type) }}
                      </span>
                    </b-td>
                    <b-td class="text-center">
                      <span class="fee-display">
                        {{ formatFeeDisplay(merchant.airtel_out, merchant.airtel_out_fee_type) }}
                      </span>
                    </b-td>
                    <b-td class="text-center">
                      <b-badge :variant="merchant.verified ? 'success' : 'secondary'">
                        <b-icon :icon="merchant.verified ? 'check-circle-fill' : 'question-circle'" />
                      </b-badge>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-row>

            <!-- Pagination -->
            <b-row v-if="showPagination" class="mt-3">
              <b-col class="d-flex justify-content-center">
                <b-pagination
                  v-model="pagination.currentPage"
                  :total-rows="pagination.totalRows"
                  :per-page="pagination.perPage"
                  @input="pageChanged"
                  size="sm"
                  pills
                  align="center"
                />
              </b-col>
            </b-row>
          </b-container>
        </transition-group>
      </b-row>
    </b-container>

    <fee-edit-modal
      ref="feeUpdateModalRef"
      @refreshMerchants="refresh"
    />
  </section>
</template>

<script>
export default {
  components: {
    "fee-edit-modal": () => import("./fee-edit.vue")
  },
  name: "FeesManagement",
  data() {
    return {
      state: {
        loading: false,
      },
      allMerchantsFees: [],
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 50,
        offset: 0,
      },
    };
  },

  computed: {
    showPagination() {
      return this.pagination.totalRows > this.pagination.perPage;
    },
  },

  mounted() {
    this.refresh();
  },

  methods: {
    formatFeeDisplay(value, feeType) {
      if (!value && value !== 0) return "-";
      if (!feeType) return "-";
      
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return "-";

      if (feeType === "FLAT") {
        return `${numValue.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        })} RFW`;
      } else if (feeType === "RATE") {
        // Smart detection: if value is very small (< 1), it's likely in decimal format
        // if value is >= 1, it's likely already in percentage format
        const percentageValue = numValue < 1 ? numValue * 100 : numValue;
        return `${percentageValue.toFixed(2)}%`;
      }
      
      return "-";
    },

    openEditFeeModal(merchant) {
      // Deep clone to prevent mutation
      const clonedMerchant = JSON.parse(JSON.stringify(merchant));
      this.$refs.feeUpdateModalRef.openModal(clonedMerchant);
    },

    async fetchMerchantFees() {
      if (this.state.loading) return;
      
      this.state.loading = true;
      
      const params = {
        offset: this.pagination.offset,
        limit: this.pagination.perPage,
      };

      try {
        const { data } = await this.axios.get(
          `internal/fees/merchants?${this.$getQueryString(params)}`
        );
        
        if (data?.merchants && Array.isArray(data.merchants)) {
          this.pagination.totalRows = data.total || 0;
          this.allMerchantsFees = data.merchants;
        } else {
          this.allMerchantsFees = [];
          this.pagination.totalRows = 0;
        }
      } catch (error) {
        this.handleError(error);
        this.allMerchantsFees = [];
      } finally {
        this.state.loading = false;
      }
    },

    handleError(error) {
      let message = "Something went wrong";
      
      if (error.errorMessage) {
        message = Array.isArray(error.errorMessage) 
          ? error.errorMessage[0] 
          : error.errorMessage;
      }
      
      this.$notify(message, "error");
    },

    refresh() {
      this.fetchMerchantFees();
    },

    pageChanged(page) {
      this.pagination.offset = this.pagination.perPage * (page - 1);
      this.pagination.currentPage = page;
      this.fetchMerchantFees();
    },

    resetPagination() {
      this.pagination = {
        currentPage: 1,
        totalRows: 0,
        perPage: 50,
        offset: 0,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.fees-table {
  .table-row-clickable {
    cursor: pointer;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: rgba(0, 123, 255, 0.1);
    }
  }
  
  .fee-display {
    font-weight: 500;
    color: #2c3e50;
  }
  
  th {
    font-weight: 600;
    color: #495057;
    border-color: #dee2e6;
  }
  
  td {
    vertical-align: middle;
    border-color: #dee2e6;
  }
}

.component-fade-enter-active, .component-fade-leave-active {
  transition: opacity 0.3s;
}

.component-fade-enter, .component-fade-leave-to {
  opacity: 0;
}

.component-group-fade-enter-active, .component-group-fade-leave-active {
  transition: all 0.3s;
}

.component-group-fade-enter, .component-group-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>